import { useEffect } from "react"
import { useQuery, makeVar } from "@apollo/client"
import { useHistory, useLocation } from "react-router-dom"
import get from "lodash/get"
import { AUTH_REDIRECT_URL_KEY } from "shared/constants"
import { IsUserLoggedIn, Account } from "./operations"

const verificationPages = [
  "/sms-verify",
  "/verify-code",
  "/sms-taken",
  "/sms-success",
  "/sms-blocked",
  "/sms-multiple"
]

// Is this dumb? Can we just use the reactive var?
export const useLoginState = () => {
  const {
    data: { isLoggedIn }
  } = useQuery(IsUserLoggedIn)
  return [isLoggedIn]
}

export const getRedirectDestination = (user, defaultUrl = "/") => {
  if (!user || user.verified) return defaultUrl

  // Check if user is verified
  // If yes redirect to Honme page
  if (!user.verified) {
    // Check if the phone number is alredy verified with another account
    if (user.phoneNumberTaken) {
      return "/sms-taken"
    }
    // No phone number, redirect to sms-verify to add one
    return "/sms-verify"
  }

  return defaultUrl
}

const isValidHttpUrl = (string) => {
  let url
  try {
    // If you only pass a relative url, you need the second argument
    // to be a valid protocol + host. We aren't using HOST anyway,
    // it's just for URL validation.
    // if `string` is a fully-qualified url, the second argument is ignored,
    // so this should work with both relative and absolute urls .
    url = new URL(string, `https://${process.env.HOST}`)
  } catch {
    return false
  }

  return url.protocol === "http:" || url.protocol === "https:"
}

const defaultUrl = "/"
const getInitialUrl = () => {
  let url = localStorage.getItem(AUTH_REDIRECT_URL_KEY)
  if (!isValidHttpUrl(url)) {
    url = defaultUrl
  }

  return url
}

export const urlAfterAuth = makeVar(getInitialUrl())

export const useAuthRedirectEffect = () => {
  const [isLoggedIn] = useLoginState()
  const history = useHistory()
  const { data: accountData, loading } = useQuery(Account, {
    skip: !isLoggedIn,
    variables: { rsvpPortalId: process.env.REACT_APP_RSVP_PORTAL_ID }
  })

  const user = get(accountData, "me", {})

  useEffect(() => {
    if (isLoggedIn && !loading && user) {
      const savedUrl = urlAfterAuth() || defaultUrl

      // Use the extracted redirection logic
      const destination = getRedirectDestination(user, savedUrl)

      history.replace(destination)
      localStorage.removeItem(AUTH_REDIRECT_URL_KEY)
      urlAfterAuth(defaultUrl)
    }
  }, [history, isLoggedIn, user, loading])

  // Return loading state for the component to use
  return { loading: isLoggedIn && loading }
}

// Hook that only handles verification redirects for logged-in users
export const useVerificationRedirectEffect = () => {
  const [isLoggedIn] = useLoginState()
  const history = useHistory()
  const location = useLocation()
  const { data: accountData, loading } = useQuery(Account, {
    skip: !isLoggedIn,
    variables: { rsvpPortalId: process.env.REACT_APP_RSVP_PORTAL_ID }
  })

  const user = get(accountData, "me", null)
  const isVerified = Boolean(user?.verified)
  const currentPath = location.pathname

  useEffect(() => {
    // If not logged in, loading, or no user, return
    if (!isLoggedIn || loading || !user) return

    // If verified, check if the current path is a verification page
    if (isVerified) {
      // If verified and currently on an any verification page -> redirect to home
      if (verificationPages.includes(currentPath)) {
        history.replace("/")
      }
      return
    }

    // Only handle verification redirects if user is logged in and data is loaded
    if (verificationPages.includes(currentPath)) {
      return
    }

    // Use the extracted redirection logic
    const destination = getRedirectDestination(user, currentPath)

    // Only redirect if the destination is different from current path
    if (destination !== currentPath) {
      history.replace(destination)
    }
  }, [isLoggedIn, user, loading, history, location.pathname])
}

export const useForceAuthEffect = (options = {}) => {
  const [isLoggedIn] = useLoginState()
  const history = useHistory()
  const location = useLocation()

  useEffect(() => {
    const defaults = {
      authSuccessUrl: `${location.pathname}${location.search}`,
      authUrl: "/signup"
    }

    const config = { ...defaults, ...options }

    if (!isLoggedIn) {
      localStorage.setItem(AUTH_REDIRECT_URL_KEY, config.authSuccessUrl)
      urlAfterAuth(config.authSuccessUrl)
      history.replace(config.authUrl)
    }
  }, [isLoggedIn, history, options, location.pathname, location.search])
}
